import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export enum OttopilotDispositionEmitWhen {
    pathMatchesKnownDisposition = "4cb7f98e-f65e-45f3-b012-849f778eb0ba",
    pathIsFinalLeaf = "6f2478b0-d2c0-4e7f-af2a-8d57b327d56f",
}

export enum OttopilotDispositionPluginKind {
    dispositionHistory = "f7f242b8-b5af-4e6d-a055-60d72d4f2e22",
    optionRules = "d7e7f5a2-c4ad-4d22-bf20-dfa42ca2ef4f",
    suggestedJourneyStatusRules = "23685a9f-0ea8-4f39-ad22-c1f0a7fd7048",
}

export enum OttopilotDispositionOptionRuleCountMode {
    totalInHistory = "f79a0ca5-4f0a-4058-9001-4f94ed0b3f85",
    consecutiveFromNewest = "2d6f8962-b766-4c96-b051-1b4cca05b209",
}

export enum OttopilotDispositionOptionRuleActionKind {
    hideWhenCountGte = "8bcb2df0-82ab-4e7e-b34f-2bc8a2f9fc41",
    setLabelWhenCountLt = "c9157e4d-0f2f-4ee8-8f74-d3ff8fcc6cb9",
    hideWhenHistoryContainsNonAllowedDispositionId = "bb3f9ce1-538f-446a-a4c6-27aa98592fc8",
    /** Hide `targetSegment` when the Event Container's current disposition equals `countDispositionId`. */
    hideWhenCurrentDispositionIdEquals = "e7c4a891-2b3d-4f6a-9c8e-1a2b3c4d5e6f",
}

export enum OttopilotSuggestedJourneyStatusRuleKind {
    dispositionIdInSet = "26ce70dc-14cf-4459-90ee-ab6bbfef6655",
    dispositionIdWithConsecutiveHistory = "6e273e10-f38a-4cb4-86e9-bb9e3ca0bf7f",
    dispositionDateWithin = "d3b6a4d2-7d9b-4af8-9b52-5c9e9b6b7e2a",
}

export type OttopilotDispositionOptionRule = {
    id: Uuid;
    when: {
        level: number;
        selectedPrefix: Array<string>;
    };
    targetSegment: string;
    countDispositionId: Uuid;
    countMode: OttopilotDispositionOptionRuleCountMode;
    action:
        | {
              kind: OttopilotDispositionOptionRuleActionKind.hideWhenCountGte;
              max: number;
          }
        | {
              kind: OttopilotDispositionOptionRuleActionKind.setLabelWhenCountLt;
              max: number;
              labelTemplate: string;
          }
        | {
              kind: OttopilotDispositionOptionRuleActionKind.hideWhenHistoryContainsNonAllowedDispositionId;
              allowedDispositionIds: Array<Uuid>;
          }
        | {
              kind: OttopilotDispositionOptionRuleActionKind.hideWhenCurrentDispositionIdEquals;
          };
};

export type OttopilotSuggestedJourneyStatusRule =
    | {
          kind: OttopilotSuggestedJourneyStatusRuleKind.dispositionIdInSet;
          dispositionIds: Array<Uuid>;
          suggested: Uuid;
      }
    | {
          kind: OttopilotSuggestedJourneyStatusRuleKind.dispositionIdWithConsecutiveHistory;
          triggerDispositionId: Uuid;
          countDispositionId: Uuid;
          consecutiveMin: number;
          suggested: Uuid;
      }
    | {
          kind: OttopilotSuggestedJourneyStatusRuleKind.dispositionDateWithin;
          triggerDispositionId: Uuid;
          timeFrom: number;
          suggested: Uuid;
      };

export type OttopilotDispositionPlugin =
    | {
          kind: OttopilotDispositionPluginKind.dispositionHistory;
          lastN: number;
      }
    | {
          kind: OttopilotDispositionPluginKind.optionRules;
          rules: Array<OttopilotDispositionOptionRule>;
      }
    | {
          kind: OttopilotDispositionPluginKind.suggestedJourneyStatusRules;
          dispositionStatusColumnId: Uuid;
          rules: Array<OttopilotSuggestedJourneyStatusRule>;
      };

export type OttopilotDispositionConfig = {
    plugins: Array<OttopilotDispositionPlugin>;
    emitWhen: OttopilotDispositionEmitWhen;
};
